/**
 * @file BulkBillDuplicateDialog.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-12
 * @modified 2026-10-12
 * @description Bulk Add Bills (2026-10-12): a document that looks like a bill
 *              already in the system (same supplier and invoice number, or same
 *              supplier, total and date — billService.findDuplicateBills), or
 *              like an earlier bill in the same batch. The choice:
 *              - Keep existing: this copy is not saved.
 *              - Replace: the existing bill is updated from this document in
 *                Edit Bill (its normal rules) and the PDF attached — greyed
 *                with the reason when that bill cannot be edited (voided, paid,
 *                a locked period, in Xero with editing there off…).
 *              - Continue as normal: saved as a new bill; the pair is flagged
 *                for everyone to review on Search Bills.
 */

import React from 'react';
import Modal from './Modal';
import EntityLink from './EntityLink';
import { useSlideOutButton } from './SlideOutButton';
import { formatPrice } from '../utils/currencyFormatter';
import { formatDate } from '../utils/dateFormatters';
import { billStatusLabel, billInXero } from '../utils/billStatus';
import { replaceBlock, sourceText } from '../utils/bulkBillPlan';
import './BulkAddBills.css';

const REASON = {
  invoice_number: 'same supplier and invoice number',
  total_and_date: 'same supplier, total and date',
};

function Footer({ onKeep, onContinue, onClose }) {
  useSlideOutButton([]);
  return (
    <div className="buttons slideout">
      <div><button className="btn-slideout" onClick={onClose}>
        <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
        <span className="btn-label">Decide later</span>
      </button></div>
      <div><button className="btn-slideout" onClick={onKeep} data-write-action
        title="This copy is not saved — the bill already in the system stays as it is">
        <div className="icon-wrapper"><span className="material-symbols-rounded">inventory</span></div>
        <span className="btn-label">Keep existing</span>
      </button></div>
      <div><button className="btn-slideout" onClick={onContinue} data-write-action
        title="Save this as a new bill as normal — both will be flagged for review on Search Bills">
        <div className="icon-wrapper"><span className="material-symbols-rounded">call_split</span></div>
        <span className="btn-label">Continue as normal</span>
      </button></div>
    </div>
  );
}

function BulkBillDuplicateDialog({ item, plan, batchOriginal = null, pushSettings = {}, firstAllowedDate = null, onChoose, onClose }) {
  if (!item) return null;
  const s = item.parsed?.suggested || {};
  const dups = item.duplicates || [];
  return (
    <Modal
      isOpen
      onClose={onClose}
      size="lg"
      className="bulk-bills-dup"
      title="This bill may already be in"
      footer={<Footer
        onClose={onClose}
        onKeep={() => onChoose({ dup_choice: 'keep' })}
        onContinue={() => onChoose({ dup_choice: 'continue' })}
      />}
    >
      <div className="bulk-bills-dup-grid">
        <div className="bulk-bills-dup-card this">
          <h4>This document</h4>
          <dl>
            <dt>From</dt><dd>{sourceText(item)}</dd>
            <dt>Supplier</dt><dd>{plan?.supplier?.name || s.supplier_name || '—'}</dd>
            <dt>Invoice no.</dt><dd>{s.supplier_reference || '—'}</dd>
            <dt>Date</dt><dd>{s.bill_date ? formatDate(s.bill_date) : '—'}</dd>
            <dt>Total</dt><dd>{s.total != null ? formatPrice(s.total) : '—'}</dd>
          </dl>
        </div>
        <div className="bulk-bills-dup-others">
          {batchOriginal && (
            <div className="bulk-bills-dup-card">
              <h4>Earlier in this batch</h4>
              <dl>
                <dt>From</dt><dd>{sourceText(batchOriginal)}</dd>
                <dt>Invoice no.</dt><dd>{batchOriginal.parsed?.suggested?.supplier_reference || '—'}</dd>
                <dt>Now</dt><dd>{batchOriginal.outcome === 'saved' ? 'Saved' : 'Not saved yet'}</dd>
              </dl>
              <p className="muted">The same invoice twice in what you uploaded. Keep existing skips this copy.</p>
            </div>
          )}
          {dups.map(d => {
            const block = replaceBlock(d, {
              switchOn: !!pushSettings.edit_in_xero_enabled, canEditPosted: !!pushSettings.can_edit_posted, firstAllowedDate,
            });
            return (
              <div className="bulk-bills-dup-card" key={d.id}>
                <h4>
                  Already in:{' '}
                  <EntityLink type="bill" id={d.id}>{d.bill_number || d.supplier_reference || `Bill #${d.id}`}</EntityLink>
                </h4>
                <dl>
                  <dt>Supplier</dt><dd>{d.supplier_name || '—'}</dd>
                  <dt>Invoice no.</dt><dd>{d.supplier_reference || '—'}</dd>
                  <dt>Date</dt><dd>{d.bill_date ? formatDate(d.bill_date) : '—'}</dd>
                  <dt>Total</dt><dd>{d.total != null ? formatPrice(d.total) : '—'}</dd>
                  <dt>Status</dt><dd>{billStatusLabel(d.status)}{d.is_queried ? ' · Queried' : ''} · {billInXero(d) ? 'in Xero' : 'not in Xero yet'}</dd>
                  <dt>PDF</dt><dd>{d.attachment_filename || 'none'}</dd>
                  <dt>Why</dt><dd>{REASON[d.reason] || d.reason}</dd>
                </dl>
                <button
                  className="btn-slideout"
                  disabled={!!block}
                  title={block || 'Update this bill from the document in Edit Bill (its normal rules), and attach this PDF'}
                  data-write-action
                  onClick={() => onChoose({ dup_choice: 'replace', existing_bill_id: d.id })}
                >
                  <div className="icon-wrapper"><span className="material-symbols-rounded">find_replace</span></div>
                  <span className="btn-label">Replace this bill…</span>
                </button>
                {block && <p className="muted">Can't replace: {block}.</p>}
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}

export default BulkBillDuplicateDialog;
